import { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AccountsAlertBanner } from './AccountsAlertBanner';
import { LayoutDashboard, Truck, Activity, Receipt, TrendingUp, Wrench, Users, MapPin, BarChart3, Settings, LogOut, User, HardHat, Menu, X, ChevronDown, ChevronRight, Wallet, ArrowLeftRight, CreditCard, Landmark, ClipboardCheck, BedDouble } from 'lucide-react';
import { CommandBar } from './CommandBar';
import { ThemeToggle } from './ThemeToggle';
import { NotificationBell } from './NotificationBell';
import { useExpenseRole } from '@/lib/useExpenseRole';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, moduleKey: 'dashboard' },
  { name: 'Cranes', href: '/cranes', icon: Truck, moduleKey: 'cranes' },
  { name: 'Operations', href: '/operations', icon: Activity, moduleKey: 'operations' },
  { 
    name: 'Finance', 
    icon: Wallet,
    moduleKey: 'finance',
    children: [
      { name: 'Revenue', href: '/revenue', icon: TrendingUp },
      { name: 'Expenses', href: '/expenses', icon: Receipt },
      { name: 'Room Rent / Conveyance / Elec & Water', href: '/room-rent', icon: BedDouble },
      { name: 'Payments', href: '/payments', icon: CreditCard },
      { name: 'Loans', href: '/loans', icon: Landmark },
      { name: 'Cash Flow', href: '/cashflow', icon: ArrowLeftRight },
    ]
  },
  { name: 'Maintenance', href: '/maintenance', icon: Wrench, moduleKey: 'maintenance' },
  { name: 'Clients', href: '/clients', icon: Users, moduleKey: 'clients' },
  { name: 'Sites', href: '/sites', icon: MapPin, moduleKey: 'sites' },
  { name: 'Employees', href: '/employees', icon: HardHat, moduleKey: 'employees' },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, moduleKey: 'analytics' },
];

const SidebarContent = ({ user, onLogout, onNavClick, expandedMenus, toggleMenu }) => {
  const { is_reviewer: isReviewer } = useExpenseRole();
  const location = useLocation();
  
  const allowed = user?.allowed_modules;
  const isAdmin = user?.role === 'admin';
  const filteredNav = isAdmin || !allowed
    ? [...navigation]
    : navigation.filter(item => allowed.includes(item.moduleKey));
  
  const navItems = [...filteredNav];
  if (!isAdmin && allowed?.includes('expense_requests')) {
    navItems.push({ name: 'Submit Expense', href: '/expense-requests', icon: Receipt });
  }
  if (isAdmin) {
    navItems.push({ name: 'Users', href: '/users', icon: User });
  }
  if (isAdmin || isReviewer) {
    navItems.push({ name: 'Expense Approvals', href: '/expense-approvals', icon: ClipboardCheck });
  }
  navItems.push({ name: 'Settings', href: '/settings', icon: Settings });

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const isParentActive = (children) => {
    return children?.some(child => isActive(child.href));
  };

  return (
    <>
      <div className="p-6 border-b border-slate-700">
        <Link to="/" onClick={onNavClick} className="no-underline">
          <h1 className="text-2xl font-heading font-bold text-indigo-400 tracking-tight uppercase">
            GTNEPL ERP
          </h1>
          <p className="text-xs text-slate-400 mt-1 uppercase tracking-widest">Management System</p>
        </Link>
      </div>

      <nav className="flex-1 overflow-y-auto py-6">
        {navItems.map((item) => {
          const Icon = item.icon;
          
          // Handle items with children (like Finance)
          if (item.children) {
            const isExpanded = expandedMenus[item.name];
            const parentActive = isParentActive(item.children);
            
            return (
              <div key={item.name}>
                <button
                  onClick={() => toggleMenu(item.name)}
                  className={`w-full flex items-center justify-between px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                    parentActive
                      ? 'bg-indigo-600/20 text-indigo-300 font-bold'
                      : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                  }`}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <span className="flex items-center gap-3">
                    <Icon className="w-5 h-5" />
                    {item.name}
                  </span>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="bg-slate-900/50">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = isActive(child.href);
                      return (
                        <Link
                          key={child.name}
                          to={child.href}
                          onClick={onNavClick}
                          data-testid={`nav-${child.name.toLowerCase().replace(' ', '-')}`}
                          className={`flex items-center gap-3 pl-14 pr-6 py-2.5 text-sm font-medium transition-colors duration-200 ${
                            childActive
                              ? 'bg-indigo-600 text-white font-bold'
                              : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          <ChildIcon className="w-4 h-4" />
                          {child.name}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }
          
          // Regular nav items
          const active = isActive(item.href);
          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onNavClick}
              data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
              className={`flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors duration-200 ${
                active
                  ? 'bg-indigo-600 text-white font-bold'
                  : 'text-slate-300 hover:bg-slate-700 hover:text-white'
              }`}
            >
              <Icon className="w-5 h-5" />
              {item.name}
            </Link>
          );
        })}
      </nav>

      <div className="p-6 border-t border-slate-700">
        <div className="mb-4">
          <p className="text-xs text-slate-400 uppercase tracking-widest">Logged in as</p>
          <p className="text-sm font-medium text-white mt-1 truncate">{user?.name}</p>
          <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          <span className="inline-block mt-2 bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-1 rounded-md uppercase">
            {user?.role}
          </span>
        </div>
        <button
          onClick={onLogout}
          data-testid="logout-button"
          className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors duration-200 w-full"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </>
  );
};

export default function DashboardLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const { is_reviewer: isReviewer } = useExpenseRole();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({ Finance: true });
  const location = useLocation();
  
  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const allowed = user?.allowed_modules;
  const isAdmin = user?.role === 'admin';
  const filteredNav = isAdmin || !allowed
    ? [...navigation]
    : navigation.filter(item => allowed.includes(item.moduleKey));

  const navItems = [...filteredNav];
  if (!isAdmin && allowed?.includes('expense_requests')) {
    navItems.push({ name: 'Submit Expense', href: '/expense-requests', icon: Receipt });
  }
  if (isAdmin) {
    navItems.push({ name: 'Users', href: '/users', icon: User });
  }
  if (isAdmin || isReviewer) {
    navItems.push({ name: 'Expense Approvals', href: '/expense-approvals', icon: ClipboardCheck });
  }
  navItems.push({ name: 'Settings', href: '/settings', icon: Settings });

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const isParentActive = (children) => {
    return children?.some(child => isActive(child.href));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:left-0 bg-[hsl(var(--sidebar))] z-40">
        <SidebarContent 
          user={user} 
          onLogout={onLogout} 
          onNavClick={() => {}} 
          expandedMenus={expandedMenus}
          toggleMenu={toggleMenu}
        />
      </aside>

      {/* Mobile Header - Visible only on mobile */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-secondary z-50 flex items-center justify-between px-4 border-b border-border">
        <div className="flex items-center">
          <button 
            onClick={() => setMobileMenuOpen(true)}
            className="text-secondary-foreground p-2 -ml-2"
            data-testid="mobile-menu-btn"
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6" />
          </button>
          <Link to="/" className="text-lg font-heading font-bold text-primary ml-2 no-underline">GTNEPL ERP</Link>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell testPrefix="mobile-notification" />
          <ThemeToggle />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 z-50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`lg:hidden fixed inset-y-0 left-0 w-72 bg-[hsl(var(--sidebar))] z-50 flex flex-col transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <Link to="/" onClick={() => setMobileMenuOpen(false)} className="no-underline">
            <h1 className="text-xl font-heading font-bold text-indigo-400 tracking-tight uppercase">
              GTNEPL ERP
            </h1>
            <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest">Management System</p>
          </Link>
          <button 
            onClick={() => setMobileMenuOpen(false)}
            className="text-slate-400 hover:text-white p-1"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <nav className="flex-1 overflow-y-auto py-4">
          {navItems.map((item) => {
            const Icon = item.icon;
            
            // Handle items with children (like Finance)
            if (item.children) {
              const isExpanded = expandedMenus[item.name];
              const parentActive = isParentActive(item.children);
              
              return (
                <div key={item.name}>
                  <button
                    onClick={() => toggleMenu(item.name)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                      parentActive
                        ? 'bg-indigo-600/20 text-indigo-300 font-bold'
                        : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="w-5 h-5" />
                      {item.name}
                    </span>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                  
                  {isExpanded && (
                    <div className="bg-slate-900/50">
                      {item.children.map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = isActive(child.href);
                        return (
                          <Link
                            key={child.name}
                            to={child.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`flex items-center gap-3 pl-12 pr-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                              childActive
                                ? 'bg-indigo-600 text-white font-bold'
                                : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                            }`}
                          >
                            <ChildIcon className="w-4 h-4" />
                            {child.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            // Regular nav items
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                  active
                    ? 'bg-indigo-600 text-white font-bold'
                    : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-700">
          <div className="mb-4">
            <p className="text-[10px] text-slate-400 uppercase tracking-widest">Logged in as</p>
            <p className="text-sm font-medium text-white mt-1 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            <span className="inline-block mt-2 bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-1 rounded-md uppercase">
              {user?.role}
            </span>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors duration-200 w-full"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 min-h-screen bg-background text-foreground">
        {/* Desktop Top Bar */}
        <div className="hidden lg:flex items-center justify-between px-8 py-3 border-b border-border bg-card">
          <CommandBar />
          <div className="flex items-center gap-2">
            <NotificationBell />
            <ThemeToggle />
          </div>
        </div>
        <AccountsAlertBanner onReview={() => navigate('/expense-approvals')} />
        <Outlet />
      </main>
    </div>
  );
}
